import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useNotification } from './NotificationContext';
import { useSubscription } from './SubscriptionContext';
import { getSubscriptionMeta } from '../config/subscriptions';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [callsOpen, setCallsOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { logout } = useAuth0();
  const { isFeatureUnlocked } = useSubscription();
  const { notifications, hasNewNotification } = useNotification();
  const enableAIServices = process.env.REACT_APP_ENABLE_AI_SERVICES === 'true';
  const enableFiles = process.env.REACT_APP_ENABLE_FILES === 'true';
  
  // Check if user is admin using both auth flag and isAdmin
  const isAuthenticated = localStorage.getItem('auth') === 'true';
  const isAdmin = isAuthenticated && localStorage.getItem('isAdmin') === 'true';

  const getBellIconClass = (isNotificationLink) => {
    if (!isNotificationLink) return '';
    return hasNewNotification ? 'sidebar-bell-icon bell-animate pulse' : 'sidebar-bell-icon';
  };

  const smsMeta = getSubscriptionMeta('sms');
  const callMeta = getSubscriptionMeta('call');
  const aiMeta = getSubscriptionMeta('ai');
  const promptMeta = getSubscriptionMeta('prompt-services');
  const smsEnabled = smsMeta.enabled !== false;
  const callEnabled = callMeta.enabled !== false;
  const aiEnabled = enableAIServices && aiMeta.enabled !== false;
  const promptEnabled = promptMeta.enabled !== false;
  const smsRequiresSubscription = smsMeta.requiresSubscription !== false;
  const callRequiresSubscription = callMeta.requiresSubscription !== false;
  const aiRequiresSubscription = aiMeta.requiresSubscription !== false;
  const promptRequiresSubscription = promptMeta.requiresSubscription !== false;
  const smsUnlocked = smsRequiresSubscription ? isFeatureUnlocked('sms') : true;
  const callUnlocked = callRequiresSubscription ? isFeatureUnlocked('call') : true;
  const aiUnlocked = aiRequiresSubscription ? isFeatureUnlocked('ai') : true;
  const promptUnlocked = promptRequiresSubscription ? isFeatureUnlocked('prompt-services') : true;

  const links = [
    { to: "/dashboard/overview", icon: "📊", text: "Dashboard" },
    { to: "/dashboard", icon: "🏠", text: "Home" },
    { to: "/dashboard/profile", icon: "👤", text: "Profile" },
    { to: "/dashboard/notifications", icon: "🔔", text: "Notifications", 
      badge: notifications.filter(n => !n.read).length },
    { to: "/assistant", icon: "💬", text: "AI Assistant" }
  ];

  const filteredLinks = links.filter(link => 
    link.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      <button 
        className="collapse-btn" 
        onClick={() => setCollapsed(c => !c)} 
        title={collapsed ? 'Expand' : 'Collapse'}
      >
        {collapsed ? '»' : '«'}
      </button>
      <div className="sidebar-content">
        <h2 className="sidebar-title">
          {collapsed ? 'S' : 'Servantica'}
        </h2>
        
        {!collapsed && (
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}

        <ul className="sidebar-list">
          {filteredLinks.map((link) => (
            <li key={link.to}>
              <NavLink to={link.to}>
                <span className={getBellIconClass(link.to === "/dashboard/notifications")}>
                  {link.icon}
                </span>
                {!collapsed && <span>{link.text}</span>}
                {link.badge > 0 && !collapsed && (
                  <span className="notification-count">{link.badge}</span>
                )}
              </NavLink>
            </li>
          ))}

          {smsEnabled && (
            <li>
              <button
                className={`dropdown-toggle ${smsRequiresSubscription && !smsUnlocked ? 'locked' : ''}`}
                onClick={() => {
                  if (smsRequiresSubscription && !smsUnlocked) {
                    navigate('/dashboard/sms');
                    return;
                  }
                  setSmsOpen(!smsOpen);
                  if (!smsOpen) {
                    setFilesOpen(false);
                    setAiOpen(false);
                    setAdminOpen(false);
                    setCallsOpen(false);
                    setPromptOpen(false);
                  }
                }}
              >
                <span>📩</span>
                {!collapsed && (
                  <>
                    <span>{smsMeta.label} {(smsRequiresSubscription && !smsUnlocked) ? ` (${smsMeta.priceLabel} to unlock)` : ''}</span>
                    <span>{smsRequiresSubscription && !smsUnlocked ? '🔒' : (smsOpen ? '▼' : '▶')}</span>
                  </>
                )}
              </button>
              {smsOpen && smsUnlocked && !collapsed && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink to={{ pathname: '/dashboard/sms', search: '?mode=send' }}>
                      Send SMS
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to={{ pathname: '/dashboard/sms', search: '?mode=schedule' }}>
                      Schedule SMS
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {callEnabled && (
            <li>
              <button
                className={`dropdown-toggle ${callRequiresSubscription && !callUnlocked ? 'locked' : ''}`}
                onClick={() => {
                  if (callRequiresSubscription && !callUnlocked) {
                    navigate('/dashboard/calls');
                    return;
                  }
                  setCallsOpen(!callsOpen);
                  if (!callsOpen) {
                    setFilesOpen(false);
                    setAiOpen(false);
                    setAdminOpen(false);
                    setSmsOpen(false);
                    setPromptOpen(false);
                  }
                }}
              >
                <span>📞</span>
                {!collapsed && (
                  <>
                    <span>{callMeta.label} {(callRequiresSubscription && !callUnlocked) ? ` (${callMeta.priceLabel} to unlock)` : ''}</span>
                    <span>{callRequiresSubscription && !callUnlocked ? '🔒' : (callsOpen ? '▼' : '▶')}</span>
                  </>
                )}
              </button>
              {callsOpen && callUnlocked && !collapsed && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink to={{ pathname: '/dashboard/calls', search: '?mode=call' }}>
                      Make Call
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to={{ pathname: '/dashboard/calls', search: '?mode=schedule' }}>
                      Schedule Call
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {enableFiles && (
            <li>
              <button
                className="dropdown-toggle"
                onClick={() => {
                  setFilesOpen(!filesOpen);
                  if (!filesOpen) {
                    setAiOpen(false);
                    setAdminOpen(false);
                    setSmsOpen(false);
                    setCallsOpen(false);
                    setPromptOpen(false);
                  }
                }}
              >
                <span>📂</span>
                {!collapsed && (
                  <>
                    <span>Files</span>
                    <span>{filesOpen ? '▼' : '▶'}</span>
                  </>
                )}
              </button>
              {filesOpen && !collapsed && (
                <ul className="dropdown-menu">
                  <li>
                    <NavLink to="/dashboard/file-upload">
                      Upload File
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/dashboard/file-list">
                      List Files
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/dashboard/file-preview">
                      Preview Files
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/dashboard/file-stats">
                      File Stats
                    </NavLink>
                  </li>
                </ul>
              )}
            </li>
          )}

          {isAdmin && (
            <li>
              <button
                className="dropdown-toggle"
                onClick={() => {
                  setAdminOpen(!adminOpen);
                  if (!adminOpen) {
                    setFilesOpen(false);
                    setAiOpen(false);
                    setSmsOpen(false);
                    setCallsOpen(false);
                    setPromptOpen(false);
                  }
                }}
              >
                <span>👑</span>
                {!collapsed && (
                  <>
                    <span>Admin</span>
                    <span>{adminOpen ? '▼' : '▶'}</span>
                  </>
                )}
              </button>
              {adminOpen && !collapsed && (
                <ul className="dropdown-menu">
                  <li><NavLink to="/dashboard/admin/push-message">Send Push Message</NavLink></li>
                </ul>
              )}
            </li>
          )}

          {aiEnabled && (
            <li>
              <button
                className={`dropdown-toggle ${aiRequiresSubscription && !aiUnlocked ? 'locked' : ''}`}
                onClick={() => {
                  if (aiRequiresSubscription && !aiUnlocked) {
                    navigate('/dashboard/speech-to-text');
                    return;
                  }
                  setAiOpen(!aiOpen);
                  if (!aiOpen) {
                    setFilesOpen(false);
                    setAdminOpen(false);
                    setSmsOpen(false);
                    setCallsOpen(false);
                    setPromptOpen(false);
                  }
                }}
              >
                <span>🤖</span>
                {!collapsed && (
                  <>
                    <span>{aiMeta.label} {(aiRequiresSubscription && !aiUnlocked) ? ` (${aiMeta.priceLabel} to unlock)` : ''}</span>
                    <span>{aiRequiresSubscription && !aiUnlocked ? '🔒' : (aiOpen ? '▼' : '▶')}</span>
                  </>
                )}
              </button>
              {aiOpen && aiUnlocked && !collapsed && (
                <ul className="dropdown-menu">
                  <li><NavLink to="/dashboard/speech-to-text">Speech to Text</NavLink></li>
                  <li><NavLink to="/dashboard/text-to-speech">Text to Speech</NavLink></li>
                  <li><NavLink to="/dashboard/face-detection">Face Detection</NavLink></li>
                  <li><NavLink to="/dashboard/image-analysis">Image Analysis</NavLink></li>
                </ul>
              )}
            </li>
          )}

          {promptEnabled && (
            <li>
              <button
                className={`dropdown-toggle ${promptRequiresSubscription && !promptUnlocked ? 'locked' : ''}`}
                onClick={() => {
                  if (promptRequiresSubscription && !promptUnlocked) {
                    navigate('/dashboard/image-generation');
                    return;
                  }
                  setPromptOpen(!promptOpen);
                  if (!promptOpen) {
                    setFilesOpen(false);
                    setAiOpen(false);
                    setAdminOpen(false);
                    setSmsOpen(false);
                    setCallsOpen(false);
                  }
                }}
              >
                <span>🪄</span>
                {!collapsed && (
                  <>
                    <span>{promptMeta.label} {(promptRequiresSubscription && !promptUnlocked) ? ` (${promptMeta.priceLabel} to unlock)` : ''}</span>
                    <span>{promptRequiresSubscription && !promptUnlocked ? '🔒' : (promptOpen ? '▼' : '▶')}</span>
                  </>
                )}
              </button>
              {promptOpen && promptUnlocked && !collapsed && (
                <ul className="dropdown-menu">
                  <li><NavLink to="/dashboard/image-generation">Text to Image</NavLink></li>
                  <li><NavLink to="/dashboard/video-generation">Video Generation</NavLink></li>
                </ul>
              )}
            </li>
          )}
        </ul>

        <div className="sidebar-footer">
          <button
            onClick={() => logout({ returnTo: window.location.origin })}
            className="logout-btn"
          >
            <span>🚪</span>
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;